import type { CreditBatch, MyCredits } from '../../services/creditsApi';
import type { CreditPack, EngineSubscription } from '../../services/billingApi';
import type { Entitlement } from '../../services/entitlementsApi';

/**
 * Itemized-credit-breakdown rules, kept OUT of CreditsMeter.tsx/
 * PlanSummaryCard.tsx so they are testable — those components (or their
 * callers) import the billing/credits APIs, which pull in Firebase, which
 * jsdom cannot load (house rule: RTL leaf components only).
 *
 * Mirrors backend/app/services/credit_service.py's `CreditBatchItem`
 * sources exactly — pinned by tests/test_credit_batch_parity.py.
 */
export const CREDIT_BATCH_SOURCES = ['base_plan', 'topup', 'upgrade', 'grant'];

/** Base item first (it REFRESHES rather than being lost, so it answers
 *  "what keeps going" ahead of "what do I lose next"); the rest ordered
 *  soonest-expiring first. Defensive client-side re-sort — the backend
 *  already orders this way, but a UI must never trust wire order alone. */
export function orderBatches(batches: CreditBatch[]): CreditBatch[] {
  const base = batches.filter((b) => b.source === 'base_plan');
  const rest = batches
    .filter((b) => b.source !== 'base_plan')
    .slice()
    .sort((a, b) => {
      const byExpiry = new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
      if (byExpiry !== 0) return byExpiry;
      return new Date(a.granted_at).getTime() - new Date(b.granted_at).getTime();
    });
  return [...base, ...rest];
}

/** Human date in IST, e.g. "02-Dec-2026" — always carries the year. A bare
 *  "2 Dec" read, out of context, like it meant "refreshes every month on
 *  the 2nd" rather than one specific date; spelling the year out removes
 *  that ambiguity. Null for a missing/unparseable value, same contract as
 *  activation.ts's formatFirstChargeDate — never print "Invalid Date". */
export function formatCreditDate(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    })
    .replace(/ /g, '-');
}

export interface BatchLine {
  label: string;
  dateLabel: string; // "renews 20-Nov-2026" / "expire on 20-Nov-2026" — empty if unparseable
}

/** Per-source label + date-label. Unknown sources (a future engine value)
 *  fall back to a generic line rather than crashing — the UI must never
 *  break on a batch it doesn't recognise yet. */
export function batchLine(item: CreditBatch, now: Date = new Date()): BatchLine {
  const d = formatCreditDate(item.expires_at, now);
  const dateSuffix = d ?? '';
  switch (item.source) {
    case 'base_plan':
      return { label: `${item.amount} plan credits`, dateLabel: dateSuffix ? `renews ${dateSuffix}` : '' };
    case 'topup':
      return { label: `+${item.amount} top-up credits`, dateLabel: dateSuffix ? `expire on ${dateSuffix}` : '' };
    case 'upgrade':
      return { label: `+${item.amount} upgrade bonus`, dateLabel: dateSuffix ? `expire on ${dateSuffix}` : '' };
    case 'grant':
      return {
        label: `+${item.amount} — ${item.reason ?? 'bonus credits'}`,
        dateLabel: dateSuffix ? `expire on ${dateSuffix}` : '',
      };
    default:
      return { label: `+${item.amount} credits`, dateLabel: dateSuffix ? `expire on ${dateSuffix}` : '' };
  }
}

/** The base_plan item's own expiry (its next refresh date), or null if
 *  there isn't one — caller falls back to `cycle_resets_on`.
 *
 *  The fallback string is explicitly UTC-suffixed ('Z'): `cycle_resets_on`
 *  is a plain calendar date with no time-of-day meaning (creditsApi.ts's
 *  own comment: "it's a UTC calendar date"), so parsing it WITHOUT a 'Z'
 *  lets `new Date(...)` fall back to the BROWSER's local timezone — for a
 *  lawyer on a machine set to UTC+8 or later, that silently rolls the
 *  displayed date back a day once reformatted in Asia/Kolkata. Anchoring it
 *  to UTC midnight first keeps the +5:30 reformat from ever crossing a day
 *  boundary, regardless of the viewer's own timezone. */
export function baseRefreshDate(credits: MyCredits, now: Date = new Date()): string | null {
  const base = credits.batches?.find((b) => b.source === 'base_plan');
  return formatCreditDate(base?.expires_at ?? null, now) ?? formatCreditDate(`${credits.cycle_resets_on}T00:00:00Z`, now);
}

/** Gate for the "Buy AI credits" CTA — deliberately NARROWER than the
 *  engine's own `_LIVE_STATUSES` (active|past_due|suspended|paused):
 *  past_due/suspended already show "Fix payment" as the priority action,
 *  and paused gets nothing extra. A top-up needs an existing live PAID
 *  subscription, never a trial. */
export function canOfferTopup(args: {
  ent: Entitlement;
  sub: EngineSubscription | undefined;
  credits: MyCredits | null;
  packs: CreditPack[];
  billingConfigured: boolean;
}): boolean {
  const { ent, sub, credits, packs, billingConfigured } = args;
  return (
    billingConfigured &&
    !!ent.vertical &&
    ent.status === 'active' &&
    sub?.status === 'active' &&
    credits?.metered === true &&
    packs.length > 0
  );
}

/** True once a FRESH topup batch has landed — granted at or after `since`
 *  (captured right before checkout started). Used to poll for a top-up's
 *  arrival the same way activation.ts's `isActivated` polls for a plan
 *  purchase. Returns false (never throws) when `batches` is absent — an
 *  older backend mid-deploy has nothing reliable to poll for; the caller
 *  degrades to a single "credits will appear shortly" message instead. */
export function hasNewTopupBatch(credits: MyCredits, since: Date): boolean {
  if (!credits.batches) return false;
  return credits.batches.some(
    (b) => b.source === 'topup' && new Date(b.granted_at).getTime() >= since.getTime(),
  );
}
